import { COMPASS_PLACES_DEFAULT, COMPASS_PLACES_MOST } from "odyssey-shared";
import type { MinimapStore } from "./store.ts";

/**
 * The storage key the Places on the compass choice lives under (P1.9-T189): per device.
 */
export const COMPASS_PLACES_KEY = "odyssey.compass.places";

/**
 * Reads a stored value as the Places on the compass choice: how many of the nearest known places the compass band
 * points to, 0 (Off) to `COMPASS_PLACES_MOST`.
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns The stored whole number from 0 to 12, else `COMPASS_PLACES_DEFAULT` for nothing, a fraction, a negative or a
 *   number past the most.
 */
export function parseCompassPlaces(raw: string | null): number {
  if (raw === null || !/^\d{1,2}$/.test(raw)) {
    return COMPASS_PLACES_DEFAULT;
  }
  const count = Number(raw);
  return count <= COMPASS_PLACES_MOST ? count : COMPASS_PLACES_DEFAULT;
}

/**
 * The page's Places on the compass choice (P1.9-T189): how many known places the compass band points to, held by the
 * minimap's store, which keeps each change on this device, so Settings reads and sets it as it does the other
 * per-device choices.
 *
 * @example
 * const places = new CompassPlacesSetting(minimap);
 * places.setCount(6);
 */
export class CompassPlacesSetting {
  /**
   * @param minimap The minimap's store.
   */
  constructor(private readonly minimap: Pick<MinimapStore, "compassPlaces" | "setCompassPlaces">) {}

  /**
   * How many places the compass shows now.
   *
   * @returns The count, 0 for Off.
   */
  get count(): number {
    return this.minimap.compassPlaces();
  }

  /**
   * Takes the viewer's choice, which the compass follows at once, and keeps it.
   *
   * @param count A whole number from 0 to 12.
   */
  setCount(count: number): void {
    this.minimap.setCompassPlaces(count);
  }
}
